import axiosInstance from '../api/axiosInstance';
import { API_ENDPOINTS } from '../api/config';

export interface PricingPlan {
  id: number;
  name: string;
  slug: string;
  frequency: 'one_time' | 'weekly' | 'monthly' | 'quarterly';
  analysis_depth: 'basic' | 'standard' | 'comprehensive';
  price: string;
  currency: string;
  features: string[];
  is_active: boolean;
  sort_order: number;
}

export interface InsightSubscription {
  id: number;
  plan: PricingPlan;
  status: 'pending' | 'active' | 'expired' | 'cancelled';
  start_date: string | null;
  end_date: string | null;
  next_generation_date: string | null;
  generations_used: number;
  max_generations: number | null;
  created_at: string;
}

export interface PaymentTransaction {
  id: number;
  txn_id: string;
  payu_txn_id: string;
  amount: string;
  currency: string;
  status: 'initiated' | 'success' | 'failed' | 'refunded';
  payment_mode: string;
  plan_name: string | null;
  credit_pack_name: string | null;
  appointment_id: number | null;
  appointment_doctor_name: string | null;
  product_type: 'subscription' | 'credit_pack' | 'gym_voucher' | 'appointment' | 'other';
  has_invoice: boolean;
  created_at: string;
}

export interface PayUFormData {
  key: string;
  txnid: string;
  amount: string;
  productinfo: string;
  firstname: string;
  email: string;
  phone: string;
  surl: string;
  furl: string;
  hash: string;
  service_provider: string;
  payu_base_url: string;
}

export interface InitiatePaymentResponse {
  transaction_id: number;
  txn_id: string;
  payu_data: PayUFormData;
}

export interface InsightSchedule {
  id?: number;
  frequency: 'weekly' | 'monthly' | 'quarterly';
  is_enabled: boolean;
  next_run_date: string | null;
  last_run_date: string | null;
  preferred_day: number;
}

export interface CreditPack {
  id: number;
  name: string;
  credits: number;
  bonus_credits: number;
  total_credits: number;
  price: string;
  currency: string;
  description: string;
  features: string[];
  sort_order: number;
}

export interface CreditCosts {
  health_insights: number;
  smart_report: number | null;
}

export interface PaymentVerification {
  txnid: string;
  status: 'success' | 'failed' | 'refunded' | 'pending';
  amount: string;
  currency: string;
  payu_txn_id: string;
  payment_mode: string;
}

export interface UserCredits {
  balance: number;
}

export interface CreditTransaction {
  id: number;
  amount: number;
  balance_after: number;
  transaction_type: 'purchase' | 'usage' | 'refund' | 'bonus';
  reference_type: string;
  reference_id: string;
  description: string;
  created_at: string;
}

const paymentService = {
  getPlans: async (): Promise<PricingPlan[]> => {
    const response = await axiosInstance.get(API_ENDPOINTS.payments.plans);
    const d = response.data; return Array.isArray(d) ? d : d.results || [];
  },

  initiatePayment: async (planId: number): Promise<InitiatePaymentResponse> => {
    const response = await axiosInstance.post(API_ENDPOINTS.payments.initiate, { plan_id: planId });
    return response.data;
  },

  getMySubscription: async (): Promise<InsightSubscription | null> => {
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.payments.mySubscription);
      return response.data;
    } catch (err: any) {
      if (err.response?.status === 404) return null;
      throw err;
    }
  },

  getTransactions: async (): Promise<PaymentTransaction[]> => {
    const response = await axiosInstance.get(API_ENDPOINTS.payments.transactions);
    const d = response.data; return Array.isArray(d) ? d : d.results || [];
  },

  getSchedule: async (): Promise<InsightSchedule | null> => {
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.insightSchedule.get);
      return response.data;
    } catch (err: any) {
      if (err.response?.status === 404) return null;
      throw err;
    }
  },

  updateSchedule: async (data: Partial<InsightSchedule>): Promise<InsightSchedule> => {
    const response = await axiosInstance.put(API_ENDPOINTS.insightSchedule.update, data);
    return response.data;
  },

  // Credit-based methods
  getCreditPacks: async (): Promise<CreditPack[]> => {
    const response = await axiosInstance.get(API_ENDPOINTS.payments.creditPacks);
    const d = response.data; return Array.isArray(d) ? d : d.results || [];
  },

  buyCredits: async (packId: number, returnPath?: string): Promise<InitiatePaymentResponse> => {
    const payload: { pack_id: number; return_path?: string } = { pack_id: packId };
    if (returnPath) payload.return_path = returnPath;
    const response = await axiosInstance.post(API_ENDPOINTS.payments.buyCredits, payload);
    return response.data;
  },

  getMyCredits: async (): Promise<UserCredits> => {
    const response = await axiosInstance.get(API_ENDPOINTS.payments.myCredits);
    return response.data;
  },

  getCreditCosts: async (): Promise<CreditCosts> => {
    const response = await axiosInstance.get(API_ENDPOINTS.payments.creditCosts);
    return response.data;
  },

  verifyTransaction: async (txnId: string): Promise<PaymentVerification> => {
    const response = await axiosInstance.get(API_ENDPOINTS.payments.verifyTransaction(txnId));
    return response.data;
  },

  getCreditHistory: async (): Promise<CreditTransaction[]> => {
    const response = await axiosInstance.get(API_ENDPOINTS.payments.creditHistory);
    const d = response.data; return Array.isArray(d) ? d : d.results || [];
  },

  downloadInvoice: async (txnId: string): Promise<void> => {
    const response = await axiosInstance.get(API_ENDPOINTS.payments.downloadInvoice(txnId), {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `invoice-${txnId}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
};

export default paymentService;
